import classes from 'classnames';
import { Helmet } from 'react-helmet';
import { useTranslation } from 'react-i18next';
import H1 from 'components/H/H1';
import { TwButton } from 'components/TwButton/TwButton';
import { Card } from '../../../../components';
import useBankIdConfirm from './useBankIdConfirm';

type ItemProps = {
  title: string;
  value?: string;
  isLarge?: boolean;
  dataTest?: string;
  skeletonClassName?: string;
};

function Item({
  title,
  value,
  isLarge = false,
  dataTest,
  skeletonClassName,
}: ItemProps) {
  const skeletonCn = `animate-pulse bg-background-accent ${
    skeletonClassName ?? ''
  }`;
  return (
    <div
      className="flex flex-col gap-2 border-b py-3 last-of-type:border-b-0"
      data-test={dataTest}
    >
      <div className="text-xs text-muted h-">{title}</div>
      <div
        className={`${value ? '' : skeletonCn} ${
          isLarge ? 'text-2xl font-bold h-8' : 'h-6'
        } `}
      >
        {value}
      </div>
    </div>
  );
}

function BankIdConfirm() {
  const {
    isPending,
    isLoading,
    info,
    handleRejectedBankID,
    handleConfirmBankID,
  } = useBankIdConfirm();
  const { t } = useTranslation('bank_id_confirm');

  const items = [
    {
      dataTest: 'name',
      title: t('form.full_name'),
      value: info?.fullName,
      isLarge: true,
      skeletonClassName: 'w-1/3',
    },

    {
      dataTest: 'full-age',
      title: t('form.full_age'),
      value: info?.fullAge,
      skeletonClassName: 'w-1/4',
    },
    {
      dataTest: 'personal-id',
      title: t('form.personal_Id'),
      value: info?.birthnumber
        ? `
                    ${info.birthnumber
                      .toString()
                      .slice(0, 6)}/${info.birthnumber.toString().slice(6)}
                  `
        : '',
      skeletonClassName: 'w-1/2',
    },
    {
      dataTest: 'id-card-number',
      title: t('form.card_number'),
      value: info?.cardNumber,
      skeletonClassName: 'w-1/4',
    },
    {
      dataTest: 'address',
      title: t('form.address'),
      value: info?.fullAddress,
      skeletonClassName: 'w-3/4',
    },
  ];

  const paddingLayout = 'px-6 lg:px-0';

  return (
    <div data-test="bank-id-confirm">
      <Helmet>
        <title>{t('html_title')}</title>
      </Helmet>
      <Card
        className={classes(
          'flex flex-col pb-0 items-center lg:border-none lg:bg-[#f4f7fa] lg:items-start',
          paddingLayout,
        )}
      >
        <H1>{t('title')}</H1>
        <p className="mb-6 text-center lg:text-left max-w-[335px] lg:max-w-none justify-self-center lg:justify-self-start">
          {t('info')}
        </p>
      </Card>
      <Card className="flex flex-col pt-0">
        {items.map(({ title, dataTest, value, isLarge, skeletonClassName }) => (
          <Item
            key={title}
            title={title}
            dataTest={dataTest}
            value={value}
            isLarge={isLarge}
            skeletonClassName={skeletonClassName}
          />
        ))}
        <TwButton
          className="mt-6"
          onClick={handleConfirmBankID}
          pending={isPending}
          data-test="continue"
          disabled={isLoading || isPending}
        >
          {t('btn')}
        </TwButton>
      </Card>

      <button
        type="button"
        className={classes(
          'text-primary mt-4 text-center lg:text-left mb-10 hover:underline',
          paddingLayout,
        )}
        onClick={handleRejectedBankID}
        data-test="skip"
        disabled={isPending}
      >
        {t('link_text')}
      </button>
    </div>
  );
}

export default BankIdConfirm;
